import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { useAuth } from "../../contexts/AuthContext";
import { useProfile } from "../../contexts/ProfileContext";
import config from "../../config";
import { CircularProgress } from "@mui/material";

// Import Images
import house_icon from "../../assets/images/auth_home_icon.webp";
import Brand_icon from "../../assets/images/Brand_icon.png";

import axios from "axios";
import { FaApple, FaFacebook, FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";

const FACEBOOK_APP_ID = "974991728146351";
import AppleLogin from "react-apple-login";
import { showErrorToast, showSuccessToast } from "../utils";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

const Login = (props) => {
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isRememberMe, setIsRememberMe] = useState(true);
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const { loginUserSuccess } = useAuth();
    const { fetchProfile } = useProfile();

    const clearError = useCallback(() => {
        setErrorMsg("");
    }, []);

    useEffect(() => {
        clearError();
    }, [clearError]);

    useEffect(() => {
        // Check both localStorage (remember me) and sessionStorage (session only)
        const token = localStorage.getItem("authUser") || sessionStorage.getItem("authUser");
        if (token) {
            navigate("/properties");
        }
    }, [navigate]);

    useEffect(() => {
        clearError();
    }, [clearError]);

    useEffect(() => {
        document.title = `Login | ${config.get("COMPANY_NAME")}`;
    }, []);

    const handleLogin = async () => {
        if (!email || !password) {
            showErrorToast("Please enter both email and password", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            setErrorMsg("Please enter email and password");
            return;
        }
        setLoading(true);
        setErrorMsg("");

        try {
            await loginApi({ email, password });
        } catch (error) {
            showErrorToast(error.message || "Invalid email or password", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            setErrorMsg(error.message || "Invalid email or password", {
                autoClose: 3000,
                pauseOnHover: false,
            });
        } finally {
            setLoading(false);
        }
    };

    const loginApi = async (payload) => {
        const response = await fetch(`${config.get("API_URL")}/user/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            if (response.status === 409) {
                toast.success("Account already exists, we have sent you a verification email, please verify.");
                return navigate("/Verify-otp", { state: { email: email, from: "verifyemail" } });
            }
            const errorData = await response.json(); // Parse the JSON error response
            const errorMessage = errorData.detail || response.statusText; // Get the actual error message
            showErrorToast(errorMessage, {
                autoClose: 3000,
                pauseOnHover: false,
            });
            setErrorMsg(errorMessage);
            setLoading(false);
            if (errorMessage === "Verify user to login! OTP has been sent to your email.") {
                navigate("/Verify-otp", { state: { email: email, from: "verifyemail" } });
            }
            return;
        }

        const data = await response.json();
        if (data.action_required === "email_verification") {
            showSuccessToast("Your email is not verified. An OTP has been sent to your email. Please verify to continue.");
            navigate("/Verify-otp", { state: { email: email, from: "verifyemail" } });
            return;
        }
        if (data.detail) {
            showErrorToast(data.detail);
            setErrorMsg(data.detail);
        } else if (data.access_token) {
            showSuccessToast("Login successful", {
                autoClose: 3000,
                pauseOnHover: false,
            });

            // Store tokens based on "Remember Me" preference
            const storage = isRememberMe ? localStorage : sessionStorage;

            // Clear any existing tokens from the other storage
            if (isRememberMe) {
                sessionStorage.removeItem("authUser");
                sessionStorage.removeItem("refreshToken");
            } else {
                localStorage.removeItem("authUser");
                localStorage.removeItem("refreshToken");
            }

            // Store tokens in appropriate storage
            storage.setItem("authUser", JSON.stringify(data.access_token));
            storage.setItem("refreshToken", JSON.stringify(data.refresh_token));

            // Update AuthContext with login success
            loginUserSuccess({
                user: data.user || {},
                token: data.access_token,
            });

            // Fetch fresh profile data
            await fetchProfile();

            navigate("/properties", { replace: true });
        }
    };

    const loginWithGoogle = async (userData) => {
        try {
            const payload = {
                email: userData.email,
                google: userData.sub,
            };
            await loginApi(payload);
        } catch (error) {
            console.error("Google login error:", error);
        }
    };

    const onGoogleButtonPress = useGoogleLogin({
        onSuccess: async (response) => {
            // Get the access token from the response
            const accessToken = response.access_token;

            try {
                // Fetch user information using the access token
                const userInfoResponse = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                loginWithGoogle(userInfoResponse);
                // Handle the user information (e.g., save it in state or send it to your backend)
                // Example user info fields: userInfo.name, userInfo.email, userInfo.picture
            } catch (error) {
                console.error("Failed to fetch user info", error);
                showErrorToast("Failed to fetch user info", {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
            }
        },
        onError: (error) => {
            console.error("Google login failed", error);
            showErrorToast("Google login failed, please try again.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            setErrorMsg("Google login failed, please try again.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
        },
    });

    const responseFacebook = async (response) => {
        try {
            if (response.accessToken) {
                const userData = await fetch(
                    `https://graph.facebook.com/v19.0/me?access_token=${response.accessToken}&fields=name,email,picture`
                ).then((res) => res.json());

                if (userData.error) {
                    throw new Error(userData.error.message);
                }

                const payload = {
                    email: userData.email,
                    facebook: userData.id,
                };
                await loginApi(payload);
            } else {
                throw new Error("Facebook login failed - no access token");
            }
        } catch (error) {
            console.error("Facebook login error:", error);
            showErrorToast(error.message || "Facebook login failed, please try again.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            setErrorMsg(error.message || "Facebook login failed, please try again.");
        }
    };
    return (
        <>
            <div className="auth-main-wrapper">
                <div
                    className="md:tw-bg-none tw-bg-center tw-bg-cover tw-w-full tw-h-full tw-flex tw-justify-between max-sm:tw-overflow-y-scroll"
                    style={{ backgroundImage: `url(${house_icon})` }}
                >
                    {/* <div className="tw-w-[100%] md:tw-w-[100%]  md:tw-z-0">
                        <img src={house_icon} className="tw-object-cover tw-h-[450px] md:tw-h-screen tw-w-full" alt="home" />
                    </div> */}
                    <div
                        style={{ backgroundImage: `url(${house_icon})` }}
                        className="max-sm:tw-hidden tw-relative tw-bg-center tw-bg-cover tw-w-full tw-h-full"
                    >
                        <div className="tw-absolute tw-inset-0 tw-bg-black tw-opacity-30"></div>

                        <div className="tw-absolute tw-top-[40px] tw-left-[30px] tw-flex tw-gap-2 tw-z-10">
                            <img
                                src={Brand_icon}
                                alt={`${config.get("COMPANY_NAME")} logo`}
                                className="logo tw-w-[35px] tw-h-[35px]"
                            />
                            <span className={`brandName tw-text-[16px] `}>{config.get("COMPANY_NAME")}</span>
                        </div>
                    </div>
                    <div className="tw-h-full max-sm:tw-h-fit tw-w-full tw-self-end tw-pb-[70px] md:tw-pb-0 max-sm:tw-text-white max-sm:tw-bg-blur_background max-sm:tw-backdrop-blur-lg max-sm:tw-rounded-tl-[40px] max-sm:tw-rounded-tr-[40px]  md:tw-px-10 max-sm:tw-px-5  max-sm:tw-mt-52 tw-flex tw-items-center tw-justify-center tw-z-10 ">
                        <div className="md:tw-max-w-[526px] 2xl:tw-max-w-[721px]  tw-w-full max-sm:tw-mt-10 max-sm:tw-mb-3">
                            <div className="">
                                <div className="tw-flex tw-flex-col tw-gap-4  2xl:tw-gap-6">
                                    {/* Title */}
                                    <div className="mb-1 tw-flex tw-flex-col tw-gap-[10px]">
                                        <h1 className="tw-text-[36px] tw-font-semibold tw-text-center 2xl:tw-text-[50px] max-sm:tw-text-[24px] ">
                                            Log in to {config.get("COMPANY_NAME")}
                                        </h1>
                                        <p className="tw-text-[16px] tw-text-center 2xl:tw-text-[20px] md:tw-text-semi_dark_text max-sm:tw-font-thin">
                                            Access your dashboard and manage your real estate activities effortlessly.
                                        </p>
                                    </div>
                                    {/* Input fields */}
                                    <div className=" tw-flex tw-flex-col tw-gap-3 2xl:tw-gap-6">
                                        <div className="tw-flex tw-flex-col tw-gap-1 2xl:tw-gap-3">
                                            <span className="tw-text-[15px] max-sm:tw-text-[13px] max-sm:tw-font-thin">Email Address</span>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Email Address"
                                                className="tw-px-3 tw-py-3 tw-bg-background_color max-sm:tw-bg-transparent tw-w-full tw-placeholder-placeholder_color tw-outline-none tw-rounded-[10px] tw-h-[50px] tw-border tw-border-border_color 2xl:tw-text-[22px] max-sm:tw-text-white"
                                            />
                                        </div>

                                        <div className=" tw-w-full tw-flex tw-flex-col tw-gap-1 2xl:tw-gap-3">
                                            <span className="tw-text-[15px] max-sm:tw-text-[13px] max-sm:tw-font-thin">Password</span>
                                            <div className="tw-relative ">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    placeholder="Password"
                                                    className="tw-px-3 tw-pr-12 tw-py-3 tw-bg-background_color max-sm:tw-bg-transparent tw-w-full tw-placeholder-placeholder_color tw-outline-none tw-rounded-[10px] tw-h-[50px] tw-border tw-border-border_color 2xl:tw-text-[22px] max-sm:tw-text-white"
                                                />
                                                {/* Toggle Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2"
                                                >
                                                    {showPassword ? <FaRegEye /> : <FaRegEyeSlash />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="tw-flex tw-mt-4 max-sm:tw-px-1 tw-justify-between tw-items-center tw-w-full">
                                            <div className="max-sm:tw-flex max-sm:tw-items-center max-sm:tw-gap-1">
                                                <input
                                                    type="checkbox"
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "var(--color-secondary) !important",
                                                        },
                                                    }}
                                                    className="tw-p-0 max-sm:tw-text-white "
                                                    checked={isRememberMe}
                                                    onChange={() => setIsRememberMe(!isRememberMe)}
                                                />
                                                <span className="tw-text-[14px] max-sm:tw-text-[12px] md:tw-ml-2  max-sm:tw-font-thin tw-text-center md:tw-mt-5 2xl:tw-text-[18px]">
                                                    Remember me?
                                                </span>
                                            </div>
                                            <a
                                                href="/forget-password"
                                                className="text-muted tw-text-[14px] tw-text-center tw-text-primary tw-underline hover:tw-text-primary_hover 2xl:tw-text-[18px]"
                                            >
                                                Forgot Password?
                                            </a>
                                        </div>
                                    </div>
                                    {/* Social Media Handles */}
                                    <>
                                        <div className="tw-flex tw-justify-center tw-w-full tw-items-center tw-gap-2  tw-text-[13px]">
                                            <div className="max-sm:tw-hidden tw-h-[1px] tw-border-[#D0D5DD] tw-w-[100px] tw-border-b-1 tw-bg-white"></div>
                                            <p className="2xl:tw-text-[22px] tw-text-[16px]  md:tw-font-semibold">Or continue with</p>
                                            <div className="max-sm:tw-hidden tw-h-[1px] tw-border-[#D0D5DD] tw-w-[100px] tw-border-b-1 tw-bg-white"></div>
                                        </div>

                                        <div className="tw-w-full tw-flex tw-justify-center">
                                            <div className="tw-flex max-sm:tw-w-fit tw-text-[8px] md:tw-text-[14px] tw-gap-5  tw-text-black tw-items-center tw-text-center tw-justify-center ">
                                                <button
                                                    onClick={onGoogleButtonPress}
                                                    className="tw-bg-icon_background  tw-w-fit tw-justify-center tw-p-2  max-sm:tw-w-full tw-rounded-full tw-flex md:tw-gap-2 tw-items-center"
                                                >
                                                    <FcGoogle className="tw-text-[20px] max-sm:tw-text-[15px] tw-object-contain" />
                                                </button>
                                                <FacebookLogin
                                                    appId={FACEBOOK_APP_ID}
                                                    autoLoad={false}
                                                    scope="public_profile,email"
                                                    callback={responseFacebook}
                                                    render={(renderProps) => (
                                                        <button
                                                            onClick={renderProps.onClick}
                                                            className="tw-bg-icon_background  tw-w-fit tw-justify-center tw-p-2 max-sm:tw-w-full tw-rounded-full tw-flex md:tw-gap-2 tw-items-center"
                                                        >
                                                            <FaFacebook className="tw-text-[20px] max-sm:tw-text-[15px] tw-text-blue-500 tw-object-contain" />
                                                        </button>
                                                    )}
                                                />
                                                <AppleLogin
                                                    clientId="com.airebrokers.admin"
                                                    redirectURI="https://admin.airebrokers.com/login"
                                                    usePopup={true}
                                                    callback={async (response) => {
                                                        const { email, sub } = await jwtDecode(response.authorization?.id_token);

                                                        const payload = {
                                                            applekey: sub,
                                                            email: email,
                                                        };
                                                        loginApi(payload);
                                                    }} // Catch the response
                                                    scope="email name"
                                                    responseMode="query"
                                                    onFailure={(error) => {
                                                        console.error("Apple Login Error:", error);
                                                        showErrorToast("Apple login failed, please try again.", {
                                                            autoClose: 3000,
                                                            pauseOnHover: false,
                                                        });
                                                        setErrorMsg("Apple login failed, please try again.");
                                                    }}
                                                    render={(
                                                        renderProps //Custom Apple Sign in Button
                                                    ) => (
                                                        <button
                                                            // onClick={loginWithApple}
                                                            onClick={renderProps.onClick}
                                                            className="tw-bg-icon_background  tw-w-fit tw-justify-center tw-p-2 max-sm:tw-w-full tw-rounded-full tw-flex md:tw-gap-2 tw-items-center"
                                                        >
                                                            <FaApple className="tw-text-[20px] max-sm:tw-text-[15px] tw-object-contain" />
                                                        </button>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </>
                                    {/* Login Button */}
                                    <div className="tw-flex tw-flex-col">
                                        <button
                                            onClick={handleLogin}
                                            className={`tw-bg-primary hover:tw-bg-primary_hover tw-border-none tw-text-white tw-mt-5 tw-p-3 tw-rounded-[9px] 2xl:tw-text-[22px] ${loading ? 'tw-opacity-70 tw-cursor-not-allowed' : ''}`}
                                            disabled={loading}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : "Log In"}
                                        </button>
                                    </div>
                                    <a
                                        href="/register"
                                        className=" tw-w-full 2xl:tw-text-[20px] tw-flex tw-justify-center tw-gap-1 tw-no-underline tw-text-[14px] "
                                    >
                                        Don't have an account?
                                        <span className="tw-text-primary hover:tw-text-primary_hover">Create free account</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
